import React, { useReducer, useRef, useEffect } from "react";
import PropTypes from "prop-types";

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const ENDPOINTS = [
    { key: "from", label: "From" },
    { key: "to", label: "Through" },
];
const EMPTY_ENDPOINT = { year: null, quarter: null };
const INITIAL_STATE = {
    panel: "closed",
    activePicker: null,
    selectionStep: "year",
    range: { from: EMPTY_ENDPOINT, to: EMPTY_ENDPOINT },
    // A year remains provisional until its quarter is selected.
    draftYears: { from: null, to: null },
};

const getQuarterIndex = ({ year, quarter }) =>
    year * 4 + QUARTERS.indexOf(quarter);

const isSameQuarter = (first, second) =>
    first.year === second.year && first.quarter === second.quarter;

const updateQuarterRange = (range, picker, endpoint, excludeSameQuarter) => {
    let nextRange = { ...range, [picker]: endpoint };
    if (
        nextRange.from.quarter &&
        nextRange.to.quarter &&
        getQuarterIndex(nextRange.from) > getQuarterIndex(nextRange.to)
    ) {
        nextRange = { from: nextRange.to, to: nextRange.from };
    }
    if (excludeSameQuarter && isSameQuarter(nextRange.from, nextRange.to)) {
        nextRange.to = EMPTY_ENDPOINT;
    }
    return nextRange;
};

const formatEndpoint = (endpoint, draftYear) => {
    if (endpoint.quarter) return `${endpoint.year} - ${endpoint.quarter}`;
    return draftYear ? `${draftYear} -` : "Select";
};

const buildCustomPeriod = (range) => ({
    name: "range",
    displayName: `${range.from.quarter} ${range.from.year} to ${range.to.quarter} ${range.to.year}`,
    startYear: range.from.year,
    startQuarter: range.from.quarter.replace("Q", ""),
    endYear: range.to.year,
    endQuarter: range.to.quarter.replace("Q", ""),
});

const pickerReducer = (state, action) => {
    switch (action.type) {
        case "reset":
            return action.panel
                ? { ...INITIAL_STATE, panel: action.panel }
                : INITIAL_STATE;
        case "toggle":
            return {
                ...state,
                panel: state.panel === "presets" ? "closed" : "presets",
            };
        case "activate":
            return {
                ...state,
                activePicker: action.picker,
                selectionStep: "year",
            };
        case "backToYears":
            return { ...state, selectionStep: "year" };
        case "selectYear": {
            return {
                ...state,
                draftYears: {
                    ...state.draftYears,
                    [state.activePicker]: action.year,
                },
                selectionStep: "quarter",
            };
        }
        case "selectQuarter": {
            const { activePicker, draftYears, range } = state;
            const endpoint = {
                year: draftYears[activePicker],
                quarter: action.quarter,
            };
            const nextRange = updateQuarterRange(
                range,
                activePicker,
                endpoint,
                action.excludeSameQuarter
            );
            return {
                ...state,
                range: nextRange,
                draftYears: {
                    from: nextRange.from.year,
                    to: nextRange.to.year,
                },
                activePicker: "to",
                selectionStep: activePicker === "from" ? "year" : "quarter",
            };
        }
        default:
            throw new Error(`Unknown range picker action: ${action.type}`);
    }
};

const QuarterlyRangePicker = ({
    selectedReportingPeriod,
    setSelectedReportingPeriod,
    setEnableResetFilters,
    setApplyFilter,
    reportingPeriodOptions,
    placeholder = "Select Reporting Period",
    placeholderColor,
    disabled = false,
    excludeSameQuarter = false,
}) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentQuarterNumber = Math.floor(now.getMonth() / 3) + 1;
    const lastCompletedQuarter = currentQuarterNumber - 1;
    const [state, dispatch] = useReducer(pickerReducer, INITIAL_STATE);
    const { panel, range, activePicker, selectionStep, draftYears } = state;
    const dropdownRef = useRef(null);
    const isOpenReportingPeriod = panel === "presets";
    const showYears = selectionStep === "year";
    const activeYear = draftYears[activePicker];
    const yearsList = Array.from({ length: 5 }, (_, i) => currentYear - 4 + i);
    const isConfirmDisabled =
        !range.from.quarter ||
        !range.to.quarter ||
        (excludeSameQuarter && isSameQuarter(range.from, range.to));

    useEffect(() => {
        dispatch({ type: "reset" });
    }, [disabled]);

    useEffect(() => {
        if (panel === "closed") return;
        const handleClickOutside = (event) => {
            if (!dropdownRef.current?.contains(event.target)) {
                dispatch({ type: "reset" });
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [panel]);

    const toggleDropdown = () => {
        if (!disabled) dispatch({ type: "toggle" });
    };

    const applyReportingPeriod = (period) => {
        setSelectedReportingPeriod(period);
        setEnableResetFilters(true);
        setApplyFilter(true);
        dispatch({ type: "reset" });
    };

    const handleReportingPeriod = (option) => {
        if (option.name === "Custom range") {
            dispatch({ type: "reset", panel: "range" });
            return;
        }
        const startYear = option.value?.effectiveStartDt
            ? new Date(option.value.effectiveStartDt).getUTCFullYear()
            : 2021;
        const endYear = option.value?.effectiveEndDt
            ? new Date(option.value.effectiveEndDt).getUTCFullYear()
            : 2025;
        applyReportingPeriod({
            ...option,
            startYear,
            startQuarter: "1",
            endYear,
            endQuarter:
                endYear === currentYear ? String(lastCompletedQuarter) : "4",
        });
    };

    const handleConfirm = () => {
        if (isConfirmDisabled) return;
        applyReportingPeriod(buildCustomPeriod(range));
    };

    return (
        <div className="col-lg-3 col-md-6 col-sm-4 col-6 col-print">
            <label className="header pb-0" htmlFor="reporting-period">
                Reporting period
            </label>

            <div className="reporting-period" ref={dropdownRef}>
                <div
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onClick={toggleDropdown}
                    onKeyDown={(e) => {
                        if (disabled) return;
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleDropdown();
                        }
                    }}
                    aria-haspopup="listbox"
                    aria-expanded={isOpenReportingPeriod}
                    aria-disabled={disabled}
                    className={`reportTableFilter${disabled ? " disabled" : ""}`}
                >
                    <span
                        style={
                            placeholderColor
                                ? {
                                      color: selectedReportingPeriod?.displayName
                                          ? "#fff"
                                          : placeholderColor,
                                  }
                                : undefined
                        }
                    >
                        {selectedReportingPeriod?.displayName || placeholder}
                    </span>
                    <i
                        className={`fa-solid fa-angle-${
                            isOpenReportingPeriod ? "up" : "down"
                        }`}
                        aria-hidden="true"
                    />
                </div>

                {isOpenReportingPeriod && (
                    <ul className="reportingPeriod-dropdown" role="listbox">
                        {reportingPeriodOptions.map((option) => (
                            <li
                                key={option.name}
                                className={`dropdown-option ${
                                    option.name ===
                                    selectedReportingPeriod?.name
                                        ? "option-selected"
                                        : ""
                                }`}
                                role="option"
                                aria-selected={
                                    option.name ===
                                    selectedReportingPeriod?.name
                                }
                                onClick={() => handleReportingPeriod(option)}
                                tabIndex={0}
                            >
                                {option.name}
                            </li>
                        ))}
                    </ul>
                )}

                {panel === "range" && (
                    <dialog
                        className="reportingPeriod-dropdown range-picker"
                        open
                        aria-label="range picker"
                    >
                        <div className="range-header">
                            <button
                                type="button"
                                className="range-button"
                                aria-label="Go back"
                                onClick={() =>
                                    dispatch({
                                        type: "reset",
                                        panel: "presets",
                                    })
                                }
                            >
                                <i
                                    className="fa-solid fa-chevron-left"
                                    aria-hidden="true"
                                />
                            </button>
                            <span>Range</span>
                        </div>

                        <div className="Quarter-input-section mb-1">
                            {ENDPOINTS.map(({ key, label }) => (
                                <div key={key}>
                                    <label htmlFor={`${key}Picker`}>
                                        {label}
                                    </label>
                                    <button
                                        type="button"
                                        className={`quarter-input${
                                            activePicker === key
                                                ? " quarter-input-active"
                                                : ""
                                        }`}
                                        onClick={() =>
                                            dispatch({
                                                type: "activate",
                                                picker: key,
                                            })
                                        }
                                    >
                                        {formatEndpoint(
                                            range[key],
                                            draftYears[key]
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>

                        {activePicker && (
                            <div className="range-selection">
                                <div className="range-selection-heading">
                                    {showYears ? (
                                        <span>Select Year</span>
                                    ) : (
                                        <>
                                            <button
                                                type="button"
                                                className="range-button range-year-back"
                                                aria-label="Go back to year selection"
                                                onClick={() =>
                                                    dispatch({
                                                        type: "backToYears",
                                                    })
                                                }
                                            >
                                                <i
                                                    className="fa-solid fa-chevron-left"
                                                    aria-hidden="true"
                                                />
                                            </button>{" "}
                                            Select Quarter
                                        </>
                                    )}
                                </div>
                                {showYears ? (
                                    <div className="d-flex flex-wrap justify-content-between range-years">
                                        {yearsList.map((year) => {
                                            return (
                                                <button
                                                    key={year}
                                                    type="button"
                                                    className={`quarter-button ${
                                                        year === activeYear
                                                            ? "quarter-selected"
                                                            : "quarter-unSelected"
                                                    }`}
                                                    onClick={() =>
                                                        dispatch({
                                                            type: "selectYear",
                                                            year,
                                                        })
                                                    }
                                                >
                                                    {year}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="d-flex flex-wrap justify-content-between">
                                        {QUARTERS.map((quarter, index) => {
                                            const endpoint = {
                                                year: activeYear,
                                                quarter,
                                            };
                                            const isQuarterDisabled =
                                                activeYear > currentYear ||
                                                (activeYear === currentYear &&
                                                    index + 1 >=
                                                        currentQuarterNumber) ||
                                                (excludeSameQuarter &&
                                                    activePicker === "to" &&
                                                    isSameQuarter(
                                                        endpoint,
                                                        range.from
                                                    ));
                                            const isSelected = isSameQuarter(
                                                endpoint,
                                                range[activePicker]
                                            );
                                            return (
                                                <button
                                                    key={quarter}
                                                    type="button"
                                                    className={`quarter-button ${
                                                        isSelected
                                                            ? "quarter-selected"
                                                            : "quarter-unSelected"
                                                    }${
                                                        isQuarterDisabled
                                                            ? " quarter-disabled"
                                                            : ""
                                                    }`}
                                                    disabled={isQuarterDisabled}
                                                    onClick={() =>
                                                        dispatch({
                                                            type: "selectQuarter",
                                                            quarter,
                                                            excludeSameQuarter,
                                                        })
                                                    }
                                                >
                                                    {quarter}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="fs-16 d-flex confirm-range">
                            <button
                                type="button"
                                className="btn-primary confirm-report-button"
                                aria-live="polite"
                                onClick={handleConfirm}
                                disabled={isConfirmDisabled}
                            >
                                Confirm
                            </button>
                        </div>
                    </dialog>
                )}
            </div>
        </div>
    );
};

const periodValueType = PropTypes.shape({
    effectiveStartDt: PropTypes.string,
    effectiveEndDt: PropTypes.string,
});
const yearType = PropTypes.oneOfType([PropTypes.number, PropTypes.string]);

QuarterlyRangePicker.propTypes = {
    selectedReportingPeriod: PropTypes.shape({
        name: PropTypes.string,
        displayName: PropTypes.string,
        startYear: yearType,
        endYear: yearType,
        startQuarter: PropTypes.string,
        endQuarter: PropTypes.string,
        value: periodValueType,
    }),
    setSelectedReportingPeriod: PropTypes.func,
    setEnableResetFilters: PropTypes.func,
    setApplyFilter: PropTypes.func,
    reportingPeriodOptions: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            displayName: PropTypes.string,
            value: periodValueType,
        })
    ),
    placeholder: PropTypes.string,
    placeholderColor: PropTypes.string,
    disabled: PropTypes.bool,
    excludeSameQuarter: PropTypes.bool,
};

export default QuarterlyRangePicker;
